'use client'

import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  Trash2Icon,
  DatabaseIcon,
  HardDriveIcon,
  CalendarIcon,
  MessageSquareIcon,
  ServerIcon,
  ClockIcon,
  FolderArchiveIcon,
  CopyIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  ContentDetailFile,
  ContentDetailInfo,
  ContentError,
  DeleteConfirmationDialog,
  JobStatusBadge
} from '@/components/partials'
import { useBackupRun, useDeleteBackupRun, useCopyToClipboard } from '@/hooks'
import { formatDateTime, tOptional } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'
import { JobStatusEnum } from '@/enums'
import { useMeStore } from '@/store'

type PropsType = {
  id: string
}

const BackupDetailSkeleton = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-1">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-36" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>

    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-36" />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Grid 2-col: status, created_at, include_database, include_storage */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
          {/* message – full width */}
          <div className="space-y-2 md:col-span-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-48" />
          </div>
          {/* file card – full width */}
          <div className="md:col-span-2">
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
        </div>

        {/* Separator + schedule section */}
        <Skeleton className="h-px w-full" />
        <div className="space-y-4">
          <Skeleton className="h-6 w-36" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Skeleton className="h-4 w-6" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Skeleton className="h-4 w-12" />
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-5 w-14 rounded-full" />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-8" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const BackupDetail = ({ id }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canDelete = hasPermission(currentUser, 'BACKUPS', 'DELETE')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const { data: backup, isLoading, error } = useBackupRun(id)
  const { mutate: deleteBackupRun, isPending: isDeleting } = useDeleteBackupRun()

  const confirmDelete = () => {
    deleteBackupRun(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['backups'] })
        toast.success(t('deleted_successfully'))
        router.push('/backups')
      },
      onError: () => {
        toast.error(t('error_deleting'))
        setDeleteConfirmOpen(false)
      }
    })
  }

  if (isLoading) {
    return <BackupDetailSkeleton />
  }

  if (error || !backup) {
    return <ContentError error={error} listUrl="/backups" />
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => router.push('/backups')}>
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('back')}</span>
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-sm font-bold lg:text-2xl">
                <FolderArchiveIcon className="h-6 w-6" />
                {t('backup')}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground text-sm">{backup.id}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(backup.id, t('copied'))}
                  className="h-6 w-6 p-0">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          {canDelete && (
            <div className="flex flex-row items-center justify-end gap-2">
              <Button variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
                <Trash2Icon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('delete')}</span>
              </Button>
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderArchiveIcon className="h-5 w-5" />
              {t('backup_details')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">{t('status')}</p>
                <JobStatusBadge status={backup.status as JobStatusEnum} />
              </div>
              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <CalendarIcon className="h-4 w-4" />
                  {t('created_at')}
                </p>
                <p className="text-sm">{formatDateTime(backup.createdAt, locale)}</p>
              </div>
              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <DatabaseIcon className="h-4 w-4" />
                  {t('include_database')}
                </p>
                <Badge variant={backup.includeDatabase ? 'success' : 'outline'}>
                  {backup.includeDatabase ? t('yes') : t('no')}
                </Badge>
              </div>
              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <HardDriveIcon className="h-4 w-4" />
                  {t('include_storage')}
                </p>
                <Badge variant={backup.includeStorage ? 'success' : 'outline'}>
                  {backup.includeStorage ? t('yes') : t('no')}
                </Badge>
              </div>
              <div className="space-y-2 md:col-span-2">
                <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                  <MessageSquareIcon className="h-4 w-4" />
                  {t('message')}
                </p>
                <p className="text-sm">{tOptional(t, backup.message || '-')}</p>
              </div>
              <div className="space-y-2 md:col-span-2">
                {backup.file ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-3">
                    <ContentDetailFile file={backup.file} />
                  </div>
                ) : (
                  <p className="text-sm">-</p>
                )}
              </div>
            </div>

            {backup.schedule && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h2 className="flex items-center gap-2 text-lg font-semibold">
                    <ServerIcon className="h-5 w-5" />
                    {t('backup_schedule')}
                  </h2>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-sm font-medium">ID</p>
                      <button
                        type="button"
                        className="cursor-pointer font-mono text-sm underline underline-offset-4 transition-opacity hover:opacity-70"
                        onClick={() => router.push(`/backups/schedules/${backup.schedule!.id}`)}>
                        {backup.schedule.id}
                      </button>
                    </div>
                    <div className="space-y-2">
                      <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                        <ClockIcon className="h-4 w-4" />
                        {t('time')}
                      </p>
                      <p className="font-mono text-sm">
                        {String(backup.schedule.hour).padStart(2, '0')}:
                        {String(backup.schedule.minute).padStart(2, '0')}
                      </p>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <p className="text-muted-foreground text-sm font-medium">{t('days')}</p>
                      <div className="flex flex-wrap gap-1">
                        {backup.schedule.days.map(day => (
                          <Badge key={day} variant="secondary" className="text-xs">
                            {t(`day_${day.toLowerCase()}` as any)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-sm font-medium">{t('keep_count')}</p>
                      <p className="text-sm">{backup.schedule.keepCount}</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-sm font-medium">{t('enabled')}</p>
                      <Badge variant={backup.schedule.enabled ? 'success' : 'secondary'}>
                        {backup.schedule.enabled ? t('enabled') : t('disabled')}
                      </Badge>
                    </div>
                  </div>
                </div>
              </>
            )}

            <ContentDetailInfo entity={backup} />
          </CardContent>
        </Card>
      </div>

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </>
  )
}

export default BackupDetail
